import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Download, FolderTree, Link2Off, ShieldOff } from 'lucide-react';
import type { ContenidoCompartido, Nodo, NodoCompartido } from '@doc/shared';
import { api, ErrorApi } from '../../lib/api.js';
import { fecha, iconoDe, tamano } from '../../lib/formato.js';
import { VisorDocumento } from '../explorador/VisorDocumento.js';

/**
 * Lo que ve quien recibe un enlace.
 *
 * No hay sesión, ni menú, ni forma de subir un nivel: el nodo compartido es
 * todo lo que existe. La página se sostiene sola porque quien la abre puede no
 * tener idea de qué es esta plataforma — de ahí que diga quién compartió, qué
 * compartió y hasta cuándo dura.
 */
export function PaginaCompartida() {
  const { token = '' } = useParams();
  const [carpetaId, setCarpetaId] = useState<string | null>(null);
  const [viendo, setViendo] = useState<NodoCompartido | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['compartido', token, carpetaId],
    queryFn: () => api<ContenidoCompartido>(`/publico/${token}${carpetaId ? `?carpeta=${carpetaId}` : ''}`),
    retry: false,
  });

  const urlPublica = (nodoId: string, modo: 'vista' | 'descargar', version?: number) =>
    `/api/publico/${token}/archivo/${nodoId}/${modo}${version ? `?version=${version}` : ''}`;

  const descargar = (nodo: { id: string; nombre: string }) => {
    const enlace = document.createElement('a');
    enlace.href = urlPublica(nodo.id, 'descargar');
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
  };

  if (isLoading) return <div className="cargando">Abriendo el enlace…</div>;

  if (error) {
    const mensaje = error instanceof ErrorApi ? error.message : 'No se pudo abrir el enlace';
    return (
      <div className="compartido">
        <div className="compartido__caja">
          <Link2Off size={40} className="dim" />
          <h1 style={{ fontSize: '1.3rem' }}>Enlace no disponible</h1>
          <p className="muted">{mensaje}</p>
          <p className="dim" style={{ fontSize: 12 }}>
            Si lo necesitas, pídele a quien te lo envió que genere uno nuevo.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const esArchivoSuelto = data.raiz.tipo === 'ARCHIVO';
  const enVisor: NodoCompartido | null = esArchivoSuelto ? data.raiz : viendo;

  return (
    <div className="compartido">
      <header className="compartido__cabecera">
        <div className="logo">
          <FolderTree size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="compartido__titulo">{data.raiz.nombre}</div>
          <div className="compartido__meta">
            Compartido por {data.enlace.compartidoPor ?? 'un usuario'} · {data.enlace.organizacion}
            {data.enlace.expiraEn && (
              <>
                {' '}
                · <CalendarClock size={11} style={{ verticalAlign: -1 }} /> caduca el {fecha(data.enlace.expiraEn)}
              </>
            )}
          </div>
        </div>
        {!data.enlace.permiteDescarga && (
          <span className="badge" title="Quien compartió deshabilitó la descarga">
            <ShieldOff size={11} /> Solo lectura
          </span>
        )}
        {esArchivoSuelto && data.enlace.permiteDescarga && (
          <button className="btn btn--primario" onClick={() => descargar(data.raiz)}>
            <Download size={14} /> Descargar
          </button>
        )}
      </header>

      {!esArchivoSuelto && (
        <div className="compartido__cuerpo">
          {data.ruta.length > 1 && (
            <div className="migas" style={{ marginBottom: 12 }}>
              {data.ruta.map((miga, indice, todas) => (
                <span key={miga.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <button
                    className={indice === todas.length - 1 ? 'actual' : ''}
                    onClick={() => setCarpetaId(indice === 0 ? null : miga.id)}
                  >
                    {miga.nombre}
                  </button>
                  {indice < todas.length - 1 && <span className="sep">/</span>}
                </span>
              ))}
            </div>
          )}

          {data.hijos.length === 0 ? (
            <div className="vacio">Esta carpeta está vacía.</div>
          ) : (
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th style={{ width: 100 }}>Tamaño</th>
                  <th style={{ width: 170 }}>Modificado</th>
                  <th style={{ width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {data.hijos.map((h) => {
                  const { Icono, clase } = iconoDe(h);
                  return (
                    <tr
                      key={h.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => (h.tipo === 'CARPETA' ? setCarpetaId(h.id) : setViendo(h))}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <span className={`icono-tipo ${clase}`}>
                            <Icono size={15} />
                          </span>
                          {h.nombre}
                        </div>
                      </td>
                      <td className="muted">{h.tipo === 'CARPETA' ? '—' : tamano(h.bytes)}</td>
                      <td className="muted">{fecha(h.actualizadoEn)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {h.tipo === 'ARCHIVO' && data.enlace.permiteDescarga && (
                          <button
                            className="btn btn--fantasma btn--icono btn--sm"
                            title="Descargar"
                            onClick={(e) => {
                              e.stopPropagation();
                              descargar(h);
                            }}
                          >
                            <Download size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {enVisor && (
        <VisorDocumento
          nodo={aNodo(enVisor)}
          hermanos={data.hijos.filter((h) => h.tipo === 'ARCHIVO').map(aNodo)}
          alNavegar={(destino) => setViendo(data.hijos.find((h) => h.id === destino.id) ?? null)}
          alDescargar={descargar}
          alCerrar={() => (esArchivoSuelto ? undefined : setViendo(null))}
          construirUrl={urlPublica}
          rutaPreparar={(id) => `/publico/${token}/archivo/${id}/preparar`}
          permiteDescarga={data.enlace.permiteDescarga}
        />
      )}
    </div>
  );
}

/**
 * El visor trabaja con `Nodo` completo; acá solo llega lo que el enlace expone.
 * Se rellena con permisos vacíos: en esta página nadie edita nada.
 */
function aNodo(compartido: NodoCompartido): Nodo {
  return {
    ...compartido,
    padreId: null,
    heredaPermisos: true,
    creadoPor: null,
    creadoPorNombre: null,
    creadoEn: compartido.actualizadoEn,
    eliminadoEn: null,
    permisos: { leer: true, escribir: false, eliminar: false, compartir: false, gestionar: false },
  };
}
